import { useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  BIORHYTHM_CYCLE_LABELS,
  BIORHYTHM_PHASE_LABELS,
  BIORHYTHM_WINDOW_DAYS,
  biorhythmLevel,
  type Biorhythm,
  type BiorhythmCycle,
} from '@mlm/shared/coaching';
import { cn } from '@mlm/ui/lib/cn';
import { shortDay } from '../lib/format';
import { Eyebrow } from './primitives';

const W = 300;
const H = 64;
const PAD_Y = 6;

const x = (i: number, n: number) => (i / (n - 1)) * W;
/** -1 (low) at the bottom, 1 (high) at the top. */
const y = (v: number) => PAD_Y + ((1 - v) / 2) * (H - PAD_Y * 2);

/**
 * The member's cycles as small multiples: one single-line chart each, sharing
 * one day axis. One line per chart needs no colour key (the brand's
 * near-monochrome palette cannot separate several lines on one plot), and a
 * day picked on any chart is shown on all of them, so they still compare at a
 * glance. The numbers behind every line are in the table under the charts.
 */
export function BiorhythmCharts({ reading }: { reading: Biorhythm }) {
  const today = BIORHYTHM_WINDOW_DAYS;
  const [picked, setPicked] = useState<number | null>(null);
  const index = picked ?? today;
  const day = reading.window[index]!;
  const n = reading.window.length;

  const pick = (e: ReactPointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    setPicked(Math.max(0, Math.min(n - 1, Math.round(ratio * (n - 1)))));
  };

  return (
    <section className="border-b border-border px-5 py-6">
      <div className="flex items-baseline justify-between gap-3">
        <Eyebrow>{index === today ? 'Today' : shortDay(day.day)}</Eyebrow>
        {picked !== null && picked !== today && (
          <button
            type="button"
            className="eyebrow underline underline-offset-4 hover:text-ink"
            onClick={() => setPicked(null)}
          >
            Back to today
          </button>
        )}
      </div>
      <div className="mt-4 space-y-6">
        {reading.cycles.map((cycle) => (
          <CycleChart
            key={cycle}
            cycle={cycle}
            reading={reading}
            index={index}
            onPick={pick}
            onLeave={() => setPicked(null)}
          />
        ))}
      </div>
      <div className="mt-3 flex justify-between font-mono text-[0.625rem] uppercase tracking-[0.12em] text-sage-deep">
        <span>{shortDay(reading.window[0]!.day)}</span>
        <span>Today</span>
        <span>{shortDay(reading.window[n - 1]!.day)}</span>
      </div>
      <details className="mt-6">
        <summary className="eyebrow cursor-pointer hover:text-ink">Show the numbers</summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="eyebrow py-2 pr-4 font-normal">Day</th>
                {reading.cycles.map((c) => (
                  <th key={c} className="eyebrow py-2 pr-4 text-right font-normal">
                    {BIORHYTHM_CYCLE_LABELS[c]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reading.window.map((d, i) => (
                <tr key={d.day} className="border-b border-border">
                  <td className={cn('py-2 pr-4', i === today ? 'text-ink' : 'text-ink/70')}>
                    {i === today ? 'Today' : shortDay(d.day)}
                  </td>
                  {reading.cycles.map((c) => (
                    <td key={c} className="py-2 pr-4 text-right font-mono tabular-nums text-ink/80">
                      {biorhythmLevel(d.values[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

function CycleChart({
  cycle,
  reading,
  index,
  onPick,
  onLeave,
}: {
  cycle: BiorhythmCycle;
  reading: Biorhythm;
  index: number;
  onPick: (e: ReactPointerEvent<SVGSVGElement>) => void;
  onLeave: () => void;
}) {
  const n = reading.window.length;
  const today = BIORHYTHM_WINDOW_DAYS;
  const values = reading.window.map((d) => d.values[cycle]);
  const points = values.map((v, i) => `${x(i, n).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const value = values[index]!;
  const phaseLabel = index === today ? BIORHYTHM_PHASE_LABELS[reading.today[cycle].phase] : null;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="eyebrow">
          {BIORHYTHM_CYCLE_LABELS[cycle]}
          {phaseLabel && ` · ${phaseLabel}`}
        </span>
        <span className="font-mono text-sm tabular-nums text-ink">{biorhythmLevel(value)}</span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="mt-2 block h-16 w-full touch-pan-y overflow-visible"
        role="img"
        aria-label={`${BIORHYTHM_CYCLE_LABELS[cycle]} cycle over ${n} days, ${biorhythmLevel(value)} on the selected day`}
        onPointerMove={onPick}
        onPointerDown={onPick}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse') onLeave();
        }}
      >
        {/* The midline: where a cycle turns. */}
        <line
          x1={0}
          x2={W}
          y1={y(0)}
          y2={y(0)}
          stroke="rgba(16,32,26,0.14)"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        <line
          x1={x(index, n)}
          x2={x(index, n)}
          y1={0}
          y2={H}
          stroke="rgba(95,106,98,0.55)"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        <polyline
          points={points}
          fill="none"
          stroke="#10201A"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {/* The day's dot sits outside the stretched SVG so it stays round at every width. */}
      <div className="pointer-events-none relative -mt-16 h-16 w-full" aria-hidden>
        <span
          className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-verde ring-2 ring-canvas"
          style={{ left: `${(index / (n - 1)) * 100}%`, top: `${(y(value) / H) * 100}%` }}
        />
      </div>
    </div>
  );
}
